import React, { useState, useMemo } from "react";
import { getAllEntries, getDemoEntries, MOODS, MONTH_NAMES_GENITIVE } from "../api/mockData";

type Period = "week" | "month" | "all";

export function Statistics() {
  const [period, setPeriod] = useState<Period>("month");
  const allEntries = useMemo(() => getAllEntries(), []);
  const isDemo = allEntries.length < 3;
  const entries = isDemo ? getDemoEntries() : allEntries;

  const filteredEntries = useMemo(() => {
    const now = new Date();
    if (period === "week") {
      const weekAgo = new Date(now);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return entries.filter((e) => new Date(e.date) >= weekAgo);
    }
    if (period === "month") {
      const monthAgo = new Date(now);
      monthAgo.setDate(monthAgo.getDate() - 30);
      return entries.filter((e) => new Date(e.date) >= monthAgo);
    }
    return entries;
  }, [entries, period]);

  // Average mood
  const avgMood = filteredEntries.length
    ? filteredEntries.reduce((sum, e) => sum + e.mood, 0) / filteredEntries.length
    : 0;

  // Mood distribution
  const moodCounts = [0, 0, 0, 0, 0];
  filteredEntries.forEach((e) => {
    moodCounts[e.mood - 1]++;
  });
  const maxCount = Math.max(...moodCounts, 1);

  // Streak calculation
  const streak = useMemo(() => {
    if (filteredEntries.length === 0) return 0;
    let count = 1;
    const sorted = [...filteredEntries].sort((a, b) => b.date.localeCompare(a.date));
    for (let i = 1; i < sorted.length; i++) {
      const prev = new Date(sorted[i - 1].date);
      const curr = new Date(sorted[i].date);
      const diff = (prev.getTime() - curr.getTime()) / (1000 * 60 * 60 * 24);
      if (diff === 1) {
        count++;
      } else {
        break;
      }
    }
    return count;
  }, [filteredEntries]);

  // Chart data points (last N entries)
  const chartEntries = filteredEntries.slice(-14);
  const chartWidth = 100;
  const chartHeight = 48;
  const padding = 4;

  const points = chartEntries.map((entry, i) => {
    const x = chartEntries.length > 1
      ? padding + (i / (chartEntries.length - 1)) * (chartWidth - padding * 2)
      : chartWidth / 2;
    const y = chartHeight - padding - ((entry.mood - 1) / 4) * (chartHeight - padding * 2);
    return { x, y, entry };
  });

  // Build SVG path
  const linePath = points.length > 1
    ? points.reduce((path, p, i) => {
        if (i === 0) return `M ${p.x} ${p.y}`;
        const prev = points[i - 1];
        const cpx = (prev.x + p.x) / 2;
        return `${path} C ${cpx} ${prev.y}, ${cpx} ${p.y}, ${p.x} ${p.y}`;
      }, "")
    : "";

  const areaPath = linePath
    ? `${linePath} L ${points[points.length - 1].x} ${chartHeight} L ${points[0].x} ${chartHeight} Z`
    : "";

  const closestMoodEmoji = MOODS[Math.round(avgMood) - 1]?.emoji || "😐";

  return (
    <div className="animate-page-in pb-28">
      {/* Header */}
      <div className="px-5 pt-6 pb-3">
        <h1
          className="font-serif text-[2rem] font-extralight tracking-tight leading-tight"
          style={{ color: "var(--foreground)" }}
        >
          Статистика
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--muted-foreground)" }}>
          Отслеживайте динамику настроения
        </p>
      </div>

      {/* Demo notice */}
      {isDemo && (
        <div
          className="mx-5 mb-4 px-4 py-3 rounded-xl flex items-start gap-3"
          style={{
            background: "var(--mood-3-soft)",
            border: "1px solid var(--mood-3)",
          }}
        >
          <span className="text-base mt-0.5">📊</span>
          <p className="text-xs leading-relaxed" style={{ color: "var(--foreground)" }}>
            Здесь будут ваши реальные данные, когда соберётся статистика за 3+ дня.
            Пока показаны демо-данные.
          </p>
        </div>
      )}

      {/* Period selector */}
      <div className="px-5 mb-5">
        <div
          className="inline-flex rounded-xl p-1 gap-1"
          style={{ background: "var(--secondary)" }}
        >
          {([
            { value: "week" as Period, label: "Неделя" },
            { value: "month" as Period, label: "Месяц" },
            { value: "all" as Period, label: "Всё время" },
          ]).map((p) => (
            <button
              key={p.value}
              onClick={() => setPeriod(p.value)}
              className="text-xs font-medium px-3.5 py-1.5 rounded-lg transition-all duration-200"
              style={{
                background: period === p.value ? "var(--card)" : "transparent",
                color: period === p.value ? "var(--foreground)" : "var(--muted-foreground)",
                boxShadow: period === p.value ? "0 1px 3px oklch(0 0 0 / 0.06)" : "none",
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3 px-5 mb-5 stagger-children">
        <div
          className="p-3.5 rounded-2xl text-center"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          <div className="text-2xl mb-1">{closestMoodEmoji}</div>
          <div className="text-lg font-semibold" style={{ color: "var(--foreground)" }}>
            {avgMood.toFixed(1)}
          </div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--muted-foreground)" }}>
            Среднее
          </div>
        </div>
        <div
          className="p-3.5 rounded-2xl text-center"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          <div className="text-2xl mb-1">📝</div>
          <div className="text-lg font-semibold" style={{ color: "var(--foreground)" }}>
            {filteredEntries.length}
          </div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--muted-foreground)" }}>
            Записей
          </div>
        </div>
        <div
          className="p-3.5 rounded-2xl text-center"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          <div className="text-2xl mb-1">🔥</div>
          <div className="text-lg font-semibold" style={{ color: "var(--foreground)" }}>
            {streak}
          </div>
          <div className="text-[10px] uppercase tracking-wider" style={{ color: "var(--muted-foreground)" }}>
            Дней подряд
          </div>
        </div>
      </div>

      {/* Mood chart */}
      <div className="px-5 mb-5">
        <div
          className="p-4 rounded-2xl"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          <h3
            className="text-xs font-medium uppercase tracking-widest mb-4"
            style={{ color: "var(--muted-foreground)" }}
          >
            Динамика настроения
          </h3>

          {chartEntries.length > 0 ? (
            <div className="relative">
              {/* Y axis labels */}
              <div className="absolute left-0 top-0 bottom-0 flex flex-col justify-between py-1 text-[10px]" style={{ color: "var(--muted-foreground)" }}>
                <span>😄</span>
                <span>😐</span>
                <span>😢</span>
              </div>

              {/* Chart */}
              <div className="ml-6">
                <svg
                  viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                  className="w-full"
                  style={{ height: "140px" }}
                  preserveAspectRatio="none"
                >
                  {/* Grid lines */}
                  {[1, 2, 3, 4, 5].map((level) => {
                    const y = chartHeight - padding - ((level - 1) / 4) * (chartHeight - padding * 2);
                    return (
                      <line
                        key={level}
                        x1={0}
                        y1={y}
                        x2={chartWidth}
                        y2={y}
                        stroke="var(--border)"
                        strokeWidth="0.3"
                        strokeDasharray="2 2"
                      />
                    );
                  })}

                  {/* Area fill */}
                  {areaPath && (
                    <path
                      d={areaPath}
                      fill="url(#moodGradient)"
                      opacity="0.3"
                    />
                  )}

                  {/* Line */}
                  {linePath && (
                    <path
                      d={linePath}
                      fill="none"
                      stroke="var(--primary)"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        strokeDasharray: 300,
                        strokeDashoffset: 0,
                        animation: "drawLine 1.5s cubic-bezier(0.22, 1, 0.36, 1) forwards",
                      }}
                    />
                  )}

                  {/* Points */}
                  {points.map((p, i) => {
                    const moodDef = MOODS.find((m) => m.value === p.entry.mood);
                    return (
                      <g key={i}>
                        <circle
                          cx={p.x}
                          cy={p.y}
                          r="1.8"
                          fill="var(--card)"
                          stroke={moodDef?.color || "var(--primary)"}
                          strokeWidth="1"
                        />
                      </g>
                    );
                  })}

                  <defs>
                    <linearGradient id="moodGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--primary)" stopOpacity="0.4" />
                      <stop offset="100%" stopColor="var(--primary)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                </svg>

                {/* X axis labels */}
                <div className="flex justify-between mt-1.5">
                  {chartEntries
                    .filter((_, i) =>
                      chartEntries.length <= 7 || i % Math.ceil(chartEntries.length / 6) === 0 || i === chartEntries.length - 1
                    )
                    .map((entry) => {
                      const d = new Date(entry.date + "T00:00:00");
                      return (
                        <span
                          key={entry.date}
                          className="text-[9px]"
                          style={{ color: "var(--muted-foreground)" }}
                        >
                          {d.getDate()} {MONTH_NAMES_GENITIVE[d.getMonth()].slice(0, 3)}
                        </span>
                      );
                    })}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <span className="text-3xl">📈</span>
              <p className="text-sm mt-2" style={{ color: "var(--muted-foreground)" }}>
                Нет данных за выбранный период
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Mood distribution */}
      <div className="px-5 mb-5">
        <div
          className="p-4 rounded-2xl"
          style={{ background: "var(--card)", border: "1px solid var(--border)" }}
        >
          <h3
            className="text-xs font-medium uppercase tracking-widest mb-4"
            style={{ color: "var(--muted-foreground)" }}
          >
            Распределение настроения
          </h3>
          <div className="space-y-3">
            {MOODS.map((m, i) => {
              const count = moodCounts[i];
              const pct = filteredEntries.length ? (count / filteredEntries.length) * 100 : 0;
              return (
                <div key={m.value} className="flex items-center gap-3">
                  <span className="text-lg w-7 text-center">{m.emoji}</span>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium" style={{ color: "var(--foreground)" }}>
                        {m.label}
                      </span>
                      <span className="text-[10px] tabular-nums" style={{ color: "var(--muted-foreground)" }}>
                        {count} ({pct.toFixed(0)}%)
                      </span>
                    </div>
                    <div
                      className="h-2 rounded-full overflow-hidden"
                      style={{ background: "var(--secondary)" }}
                    >
                      <div
                        className="h-full rounded-full transition-all duration-700 ease-out"
                        style={{
                          width: `${(count / maxCount) * 100}%`,
                          background: m.color,
                          minWidth: count > 0 ? "4px" : "0",
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes drawLine {
          from { stroke-dashoffset: 300; }
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
}
